/**
 * Welcome Page - Referral Landing Page
 * Dedicated landing page for users coming from referral links
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './WelcomePage.css';

// Icons
const GiftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="8" width="18" height="4" rx="1"/>
    <path d="M12 8V21M12 8H7.5a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8zM12 8h4.5a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8z"/>
    <rect x="3" y="12" width="18" height="9" rx="1"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

// Feature icons
const featureIcons = [
  <svg key="modes" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  <svg key="ai" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  <svg key="memory" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/><path d="M12 6v6l4 2"/></svg>,
  <svg key="content" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
];

const WelcomePage: React.FC = () => {
  const { t } = useTranslation('welcome');
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState<string | null>(null);

  const benefits = t('benefits.items', { returnObjects: true }) as Array<{ title: string; description: string }>;

  // Extract and store referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Store in sessionStorage for use during signup
      sessionStorage.setItem('referralCode', ref);
    }
  }, [searchParams]);

  const signupUrl = referralCode ? `/register?ref=${referralCode}` : '/register';

  return (
    <div className="welcome-page">
      {/* Hero Section */}
      <section className="welcome-hero">
        <div className="welcome-hero-content">
          {referralCode && (
            <div className="welcome-referral-badge">
              <GiftIcon />
              <span>{t('hero.invitedBy')}</span>
            </div>
          )}
          <h1 className="welcome-title">{t('hero.title')}</h1>
          <p className="welcome-subtitle">{t('hero.subtitle')}</p>
          <div className="welcome-hero-actions">
            <Link to={signupUrl} className="btn btn-primary btn-lg">
              {t('hero.cta')}
              <ArrowRightIcon />
            </Link>
            <Link to="/courses" className="btn btn-outline btn-lg">
              {t('hero.secondaryCta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Reward Banner */}
      {referralCode && (
        <section className="welcome-reward">
          <div className="welcome-reward-content">
            <div className="welcome-reward-icon">
              <GiftIcon />
            </div>
            <div className="welcome-reward-text">
              <h3>{t('reward.title')}</h3>
              <p>{t('reward.description')}</p>
            </div>
            <div className="welcome-reward-tokens">
              <span className="reward-label">{t('reward.yourReward')}</span>
              <span className="reward-value">{t('reward.tokens', { count: 50 })}</span>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="welcome-benefits">
        <h2 className="welcome-section-title">{t('benefits.title')}</h2>
        <div className="welcome-benefits-grid">
          {benefits.map((benefit, index) => (
            <div key={index} className="welcome-benefit-card">
              <div className="benefit-icon">
                {featureIcons[index]}
              </div>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="welcome-cta">
        <div className="welcome-cta-content">
          <CheckCircleIcon />
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.subtitle')}</p>
          <Link to={signupUrl} className="btn btn-primary btn-xl">
            {t('cta.button')}
            <ArrowRightIcon />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;
